import React from "react";
import moment from "moment";
import styled from "styled-components";
import {
  Body,
  color,
  borderRadius,
  spacing,
  elevation,
  palette,
  typography,
} from "@conserveit/blocks";
import {
  formatBody,
  formatAttachments,
  formatTransition,
} from "../../Issues/components/Comment.utils";
import { Comment as CommentType } from "./CommentsColumn";

const CommentContainer = styled.div`
  background-color: ${color.pureWhite};
  border-radius: ${borderRadius.large};
  margin-bottom: ${spacing.default};
  box-shadow: ${elevation.content};
  padding: ${spacing.default};
  overflow: hidden;
`;

const TransitionContainer = styled.p`
  padding: ${spacing.tight};
  background-color: ${palette.yellow1};
  border: 1px solid ${palette.yellow5};
  border-radius: ${borderRadius.default};
  color: ${palette.yellow9};
`;

const DateText = styled.p`
  margin-bottom: ${spacing.tight};
  color: ${palette.neutral7};
  ${typography.tiny}
`;

const BodyText = styled(Body)`
  margin-bottom: ${spacing.snug};
`;

const Author = styled.p`
  color: ${palette.neutral7};
  ${typography.bodySmall}
`;

const Separator = styled.hr`
  margin-top: ${spacing.snug};
  margin-bottom: ${spacing.snug};
  background-color: ${color.theme.divider.solid};
`;

const AttachmentList = styled.ul`
  padding: 0;
  margin: 0 0 ${spacing.snug} 0;
`;

const AttachmentListItem = styled.li`
  list-style-type: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AttachmentLink = styled.a.attrs({ target: "_blank", rel: "noopener" })`
  display: inline;
  color: ${palette.blue5};
  text-decoration: underline;
  text-decoration-style: dashed;
  text-underline-position: under;
`;

interface Props {
  comment: CommentType;
}

export const Comment: React.FC<Props> = ({ comment }) => {
  const transition = formatTransition(comment.body);
  const attachments = formatAttachments(comment.body);
  const body = formatBody(comment.body);
  const author = comment.author || comment.author_name_display;
  return (
    <CommentContainer>
      <DateText>{moment(comment.created).format("D MMMM YYYY")}</DateText>
      {transition && <TransitionContainer>{transition}</TransitionContainer>}
      {transition && body && <Separator />}
      {body && <BodyText>{body}</BodyText>}
      {attachments && (
        <AttachmentList>
          {attachments.map((attachment) => (
            <AttachmentListItem key={attachment[1]}>
              <AttachmentLink href={attachment[1]}>
                {attachment[0]}
              </AttachmentLink>
            </AttachmentListItem>
          ))}
        </AttachmentList>
      )}
      <Author>{author}</Author>
    </CommentContainer>
  );
};
